import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Per-CLAUDE.md: profile-store. Local-only for now; server-canonical lives
 * behind RFC-0006 (Student State). This store carries v0 fields the app
 * needs to personalize: identity, the cold-start outcome, an interest tag
 * for worked-example flavor, and per-topic mastery priors set by the
 * diagnostic.
 *
 * Mastery beliefs live in `progress.ts` (per-concept tier). The diagnostic
 * writes per-topic priors here AND seeds the progress store for each
 * concept in that topic.
 */
export const INTEREST_TAGS = ['default', 'sports', 'cs', 'agriculture'] as const;
export type InterestTag = (typeof INTEREST_TAGS)[number];

export type DiagnosticTopicScore = {
  topic: string;
  correct: number;
  total: number;
  /** average difficulty answered correctly (0–3 from easy→very-hard) */
  effectiveLevel: number;
};

interface ProfileState {
  studentId: string;
  name: string;
  language: 'en';
  interestTag: InterestTag;

  firstSeenAt: number;
  lastSeenAt: number;

  /** Target exam date as ISO `YYYY-MM-DD`. Drives Today's countdown. */
  examDate: string | null;
  /** Self-declared daily study budget in minutes (15..120). */
  dailyMinutesGoal: number | null;

  /** null = onboarding not yet finished. ISO timestamp = completed. */
  coldStartDoneAt: string | null;
  diagnosticScores: DiagnosticTopicScore[];
  /** Set once the diagnostic scores have been turned into per-concept mastery. */
  coldStartSeededAt: string | null;

  // ── actions ──────────────────────────────────────────────────────────
  setName: (name: string) => void;
  setInterestTag: (tag: InterestTag) => void;
  setExamDate: (iso: string | null) => void;
  setDailyMinutesGoal: (minutes: number | null) => void;
  completeColdStart: (scores: DiagnosticTopicScore[]) => void;
  markColdStartSeeded: () => void;
  touch: () => void;
  resetOnboarding: () => void;
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `dl-${Math.random().toString(36).slice(2, 10)}`;
}

const now = () => Date.now();

export const useProfile = create<ProfileState>()(
  persist(
    (set) => ({
      studentId: uuid(),
      name: '',
      language: 'en',
      interestTag: 'default',
      firstSeenAt: now(),
      lastSeenAt: now(),
      examDate: null,
      dailyMinutesGoal: null,
      coldStartDoneAt: null,
      diagnosticScores: [],
      coldStartSeededAt: null,

      setName: (name) => set({ name }),
      setInterestTag: (interestTag) => set({ interestTag }),
      setExamDate: (examDate) => set({ examDate }),
      setDailyMinutesGoal: (dailyMinutesGoal) => set({ dailyMinutesGoal }),
      completeColdStart: (diagnosticScores) =>
        set({
          diagnosticScores,
          coldStartDoneAt: new Date().toISOString(),
          coldStartSeededAt: null,
          lastSeenAt: now(),
        }),
      markColdStartSeeded: () => set({ coldStartSeededAt: new Date().toISOString() }),
      touch: () => set({ lastSeenAt: now() }),
      resetOnboarding: () =>
        set({
          coldStartDoneAt: null,
          coldStartSeededAt: null,
          diagnosticScores: [],
          interestTag: 'default',
          examDate: null,
          dailyMinutesGoal: null,
        }),
    }),
    {
      name: 'disha.profile',
      version: 3,
      migrate: (persisted, version) => {
        const p = (persisted ?? {}) as Partial<ProfileState>;
        // v1 → v2: add interestTag, coldStartDoneAt, diagnosticScores, timestamps.
        if (version < 2) {
          return {
            studentId: p.studentId ?? uuid(),
            name: p.name ?? '',
            language: 'en',
            interestTag: 'default',
            firstSeenAt: now(),
            lastSeenAt: now(),
            examDate: null,
            dailyMinutesGoal: null,
            coldStartDoneAt: null,
            diagnosticScores: [],
            coldStartSeededAt: null,
          } as Partial<ProfileState>;
        }
        // v2 → v3: add examDate, dailyMinutesGoal, coldStartSeededAt.
        if (version < 3) {
          return {
            ...p,
            examDate: null,
            dailyMinutesGoal: null,
            coldStartSeededAt: null,
          } as Partial<ProfileState>;
        }
        return p as Partial<ProfileState>;
      },
    },
  ),
);

/** Day count since firstSeenAt. 1-indexed (today = Day 1). */
export function useDayCount(): number {
  const firstSeenAt = useProfile((s) => s.firstSeenAt);
  const diffMs = Date.now() - firstSeenAt;
  return Math.max(1, Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1);
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Whole-day countdown to the student's target exam. Negative if the date has
 * passed; null if no exam date is set yet. Calendar-day delta (not 24h
 * chunks), so an exam at 9 AM tomorrow reads as "1 day" regardless of clock.
 */
export function daysUntilExam(examDate: string | null, nowMs: number = Date.now()): number | null {
  if (!examDate) return null;
  const target = new Date(`${examDate}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date(nowMs);
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / MS_PER_DAY);
}
